import {
  CooperationApplyInputSchema,
  CooperationApplyResponseSchema,
  type CooperationApplyInput,
  type CooperationApplyResponse,
} from '../contracts/cooperation';
import type { RequestFn } from './client';

/**
 * Namespace `client.cooperation` — транспорт для `POST /api/cooperation/apply`
 * (Блок 9 «Сотрудничество», ADR-0086, PR#2 задача 0.3). **ТОЛЬКО транспорт.**
 *
 * **Out of scope (явно):**
 *   - Сам эндпоинт `/api/cooperation/apply` + Turnstile (`Cf-Turnstile-Response`) +
 *     rate-limit + PDF→R2 — задача 2.2 (Фаза 2). Turnstile/rate-limit — это
 *     заголовок/Edge-логика, НЕ поле контракта и НЕ забота этого метода.
 *   - Таблица `cooperation_applications` (region-db) — Фаза 1.
 *
 * Эндпоинт ПУБЛИЧНЫЙ (без JWT — регистрация новых поставщиков), поэтому `auth: false`:
 * `Authorization` не шлём. Сервер выводит `region` из города (`CITY_REGION_MAP[city]`),
 * `ownerUserId=null`, `status='new'` — в теле их нет (§1a анти-спуфинг, P-009).
 * Относительный путь (P-013), типизированный клиент (P-012).
 *
 * P-009 защита уровня 2: client-side `safeParse` ДО сети — невалидный payload
 * (нет мессенджера, customService без 'other', город вне CITIES) отклоняется без
 * сетевого вызова. Симметрия с `feedback.create()` / `analytics.postEvents()`.
 */
export interface CooperationApi {
  /**
   * POST /api/cooperation/apply — отправить заявку поставщика (транспорт-only).
   *
   * @param input — `CooperationApplyInput` (Zod-валидирован client-side через safeParse).
   * @returns `CooperationApplyResponse` — `{ id: uuid, status: 'new' }`.
   * @throws `z.ZodError` если client-side валидация не прошла (нет сетевого вызова).
   * @throws `ApiClientError` при 400 / network / 5xx / PARSE_ERROR.
   */
  apply(input: CooperationApplyInput): Promise<CooperationApplyResponse>;
}

export function createCooperationApi(request: RequestFn): CooperationApi {
  return {
    apply: (input) => {
      const parsed = CooperationApplyInputSchema.safeParse(input);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/cooperation/apply',
        body: parsed.data,
        responseSchema: CooperationApplyResponseSchema,
        // публичный эндпоинт — без JWT (§1a; регистрация новых поставщиков)
        auth: false,
      });
    },
  };
}
